import { z } from "zod";

import type { Parser } from "../identity/live/schemas";

/**
 * Projects as the identity service sends them (`crates/services/identity/src/projects/views.rs`), checked before the
 * app uses them.
 */

const disciplineSchema = z.enum(["A", "I", "L", "U", "H", "E"]);
const accessSchema = z.enum(["view", "comment", "edit", "manage"]);
const roleSchema = z.enum(["project_admin", "editor", "commenter", "viewer"]);
/** A drawing's kind, as identity names it (`projects/input.rs` DRAWING_KINDS). */
export const drawingKindSchema = z.enum([
  "plan",
  "site",
  "section",
  "elevation",
  "detail",
  "layout",
]);

export const projectSchema = z.object({
  id: z.string().min(1),
  shortId: z.string(),
  name: z.string(),
  client: z.string(),
  type: z.string(),
  discipline: disciplineSchema,
  city: z.string(),
  state: z.string(),
  address: z.string().nullable(),
  templateId: z.string(),
  // Absent from a service older than the sample gallery and the drawing templates.
  drawingTemplate: z.string().optional(),
  sample: z.string().nullable().optional(),
  stageIndex: z.number().int().min(0),
  // Studio's open tasks, null until the project's Studio has stages; absent from a service older than it.
  studioOpenTasks: z.number().int().min(0).nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  archived: z.boolean(),
  pinned: z.boolean(),
  role: roleSchema.nullable(),
  access: accessSchema,
  canEdit: z.boolean(),
  canManage: z.boolean(),
  team: z.array(z.string()),
});

export const drawingSchema = z.object({
  id: z.string().min(1),
  drawingId: z.string(),
  /** Restricted to named people and roles (FR-COLLAB-008); a service from before sends none. */
  restricted: z.boolean().optional(),
  name: z.string(),
  kind: drawingKindSchema,
  createdAt: z.string(),
});

export const memberSchema = z.object({
  userId: z.string(),
  role: roleSchema,
  name: z.string().nullable(),
  email: z.string().nullable(),
});

export const selfAddSchema = z.object({
  id: z.string(),
  userId: z.string(),
  name: z.string(),
  initials: z.string(),
  reason: z.string(),
  at: z.string(),
});

export const detailSchema = projectSchema.extend({
  drawings: z.array(drawingSchema),
  members: z.array(memberSchema),
});

export type ServerProject = z.infer<typeof projectSchema>;
export type ServerDetail = z.infer<typeof detailSchema>;
export type ServerDrawing = z.infer<typeof drawingSchema>;

function parser<T>(schema: z.ZodType<T>): Parser<T> {
  return { parse: (value) => schema.parse(value) };
}

export const parseProject = parser(projectSchema);
export const parseProjects = parser(z.array(projectSchema));
export const parseDetail = parser(detailSchema);
export const parseDrawing = parser(drawingSchema);
export const parseSelfAdds = parser(z.array(selfAddSchema));
export const parseMembers = parser(z.array(memberSchema));

export const restrictionSchema = z.object({
  itemId: z.string(),
  kind: z.enum(["drawing", "file"]),
  people: z.array(z.string()),
  roles: z.array(z.enum(["viewer", "commenter", "editor"])),
  restrictedBy: z.string(),
  restrictedAt: z.string(),
});
export const restrictionsSchema = z.object({
  canManage: z.boolean(),
  items: z.array(restrictionSchema),
});
export const parseRestriction = parser(restrictionSchema);
export const parseRestrictions = parser(restrictionsSchema);
